import { IMinUserDepricated, IUserDepricated } from "@mctypes/global.types";
import { IIssueApi, IUserPartial } from "@schemas/issueSchema";
import { ColDef } from "ag-grid-community";
import React from "react";
import { DisplayDueDate, OptionBadge, Tags } from "../../../../../../components";
import { McMantineMultiUserSelect } from "../../../../../../components/McMantineUserSelect/McMantineUserMultiSelect";
import { CellEditFormatter } from "../../../../../../components/columnGlobal";
import { formatShortDate, isValidDateValue } from "../../../../admin/selectors";
import { IssueIdWithAksjonCompletedCount } from "../../../IssueIdWithAksjonCompletedCount";
import { doFetchUniqIssueTagsOnBoardDepricated } from "../../../issueAPIDepricated";
import {
  ISSUE_COMPLETED_VALUE,
  ISSUE_DATAFILEDS_ENUM,
  ISSUE_EXPIRED,
  ISSUE_PRIORTY_OPTIONS,
  ISSUE_SECTIONS,
  ISSUE_STATES_OPTIONS,
} from "../../../issueConstants";
import { convertValueAndColorToMultiDisplayColor } from "../../../issueUtils";
import { IssueConnectedBimSpacesSection } from "../../IssueItemModal/new/IssueConnectedBimSpacesSection";
import RefactoredExpandAllDetailRowsButton from "../ExpandAllDetailRowsButton";
import RefactoredIssueTableUserCellRenderer from "../IssueTableUserCellRenderer";
import { dateExportFormatter } from "../issueTableFormatters";
import { IssueTableColumnContext } from "./issueTableColumnContext";

export const getIssueBaseColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  ...getMetaColumns(context),
  ...getContentColumns(context),
  ...getPeopleColumns(context),
  ...getDateColumns(context),
  ...getTimeColumns(context),
];

const getMetaColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  {
    headerName: "",
    cellClass: "p-0",
    cellRenderer: (props) => {
      const { node, api, data } = props;
      const isIssue = data?.taskOrIssue === "ISSUE";

      const handleClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (!isIssue) return;

        node.setExpanded(!node.expanded);
        api.refreshCells({ force: true });
        api.setFocusedCell(node.rowIndex, "id", null);
      };

      if (!isIssue) {
        return <div style={{ width: 40 }} />;
      }

      return (
        <div
          style={{ marginLeft: "3px" }}
          onClick={handleClick}
          className={`expandIcon${node.expanded === true ? " rotateIcon" : ""}`}
        >
          <i className="fa fa-angle-down" />
        </div>
      );
    },
    width: 40,
    maxWidth: 40,
    suppressSizeToFit: true,
    suppressHeaderMenuButton: true,
    suppressColumnsToolPanel: true,
    suppressMovable: true,
    pinned: "left",
    context: ISSUE_SECTIONS.METACOLUMN,
    headerClass: "px-0 py-0",
    resizable: false,
    headerComponent: RefactoredExpandAllDetailRowsButton,
  },
  {
    field: "_id",
    headerName: "-",
    type: "alwaysHidden",
  },
  {
    field: "connecetedIssue",
    headerName: "-",
    type: "alwaysHidden",
  },
  {
    field: "connectedTasks",
    headerName: "-",
    type: "alwaysHidden",
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.ID),
    minWidth: 140,
    width: 140,
    cellClass: "grid-display-block px-1",
    cellRenderer: (params) => (
      <IssueIdWithAksjonCompletedCount
        id={params.value}
        issue={params.data as IIssueApi}
        setSelectedIssue={context.setSelectedIssue}
        showClientLock={context.boardHasClientAccess && params.data?.clientAccess !== true}
      />
    ),
  },
];

const getContentColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.TITLE),
    minWidth: 200,
    filter: "agTextColumnFilter",
    maxWidth: 800,
    autoHeight: true,
    flex: 2,
    editable: true,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.DESCRIPTION),
    autoHeight: true,
    flex: 2,
    minWidth: 200,
    maxWidth: 600,
    cellClass: "overflow-hidden",
    cellEditor: "agLargeTextCellEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      maxLength: 5000,
    },
    filter: "agTextColumnFilter",
    cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.DESCRIPTION),
    editable: true,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.CONCLUSION),
    autoHeight: true,
    flex: 2,
    minWidth: 200,
    maxWidth: 600,
    cellClass: "overflow-hidden",
    cellEditor: "agLargeTextCellEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      maxLength: 5000,
    },
    filter: "agTextColumnFilter",
    cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.CONCLUSION),
    editable: true,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.TYPEOFISSUE),
    minWidth: 80,
    cellEditor: "agRichSelectCellEditor",
    editable: true,
    filter: "agSetColumnFilter",
    cellEditorParams: {
      values: context.types ?? [],
    },
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.PNS),
    cellRenderer: (params) => {
      const pnsItem = context.pnsItems?.find((pns) => pns.props?.pnsId === params.value);
      return pnsItem != null ? `${pnsItem.props?.pnsId} - ${pnsItem.props?.title}` : params.value;
    },
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.TAGS),
    cellClass: "grid-display-block p-0",
    filter: "agSetColumnFilter",
    autoHeight: true,
    cellRenderer: (params) => (
      <Tags
        tags={params.value}
        updateCb={(tags: string[]) => context.handleUpdateIssueField(params.data._id, "tags", tags)}
        fetchUniqTags={doFetchUniqIssueTagsOnBoardDepricated}
        fetchArgs={[context.projectId, context.issueBoardId]}
      />
    ),
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.STATUS),
    editable: true,
    filter: "agSetColumnFilter",
    valueSetter: (params) => params.newValue,
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: {
      values: ISSUE_STATES_OPTIONS.map((opt) => opt.value),
      cellRenderer: (params) => (
        <OptionBadge
          activeOption={params.value}
          options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)}
          noWrap={true}
        />
      ),
      valueListMaxHeight: 220,
    },
    cellRenderer: (params) => (
      <OptionBadge activeOption={params.value} options={convertValueAndColorToMultiDisplayColor(ISSUE_STATES_OPTIONS)} />
    ),
  },
  {
    filter: "agSetColumnFilter",
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.DISCIPLIN),
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.PRIORITY),
    editable: true,
    filter: "agSetColumnFilter",
    filterParams: {
      valueFormatter: ({ value }) => ISSUE_PRIORTY_OPTIONS.find((opt) => opt.key === value)?.displayName || value,
    },
    valueSetter: (params) => params.newValue.key,
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: {
      values: ISSUE_PRIORTY_OPTIONS.map((opt) => opt.key),
      cellRenderer: (params) => <OptionBadge activeOption={params.value} options={ISSUE_PRIORTY_OPTIONS} noWrap={true} />,
      valueListMaxHeight: 220,
    },
    cellRenderer: (params) => <OptionBadge activeOption={params.value} options={ISSUE_PRIORTY_OPTIONS} />,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.REFERENCEURL),
    minWidth: 200,
    maxWidth: 400,
    cellEditor: "agLargeTextCellEditor",
    cellEditorPopup: true,
    cellEditorParams: {
      maxLength: 5000,
    },
    editable: true,
    autoHeight: true,
    cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.REFERENCEURL),
    filter: "agTextColumnFilter",
  },
  {
    headerName: context.issueBoard?.bucketNameSetting,
    field: ISSUE_DATAFILEDS_ENUM.BUCKET,
    ...context.hideAndSort(ISSUE_DATAFILEDS_ENUM.BUCKET),
    cellClass: "overflow-hidden",
    cellRenderer: (params) => CellEditFormatter(params.value, ISSUE_DATAFILEDS_ENUM.BUCKET),
    maxWidth: 250,
    editable: true,
    filter: "agSetColumnFilter",
    cellEditor: "agRichSelectCellEditor",
    cellEditorParams: {
      values: context.boardBuckets.map((bucket) => bucket.value),
      valueListMaxHeight: 220,
    },
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.CONNECTEDBIMSPACES),
    autoHeight: true,
    minWidth: 280,
    flex: 2,
    filter: "agTextColumnFilter",
    valueGetter: (params) =>
      (params.data?.connectedBimSpaces ?? [])
        .map((space) => [space.roomNumber, space.roomName, space.floor, space.building].filter(Boolean).join(" "))
        .join(", "),
    cellClass: "grid-display-block p-0",
    cellRenderer: (params) => (
      <div onClick={(event) => event.stopPropagation()}>
        <IssueConnectedBimSpacesSection issue={params.data} projectId={context.projectId} hideTitle />
      </div>
    ),
  },
];

const getPeopleColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.CREATEDBY_NAME),
    cellRenderer: (params) => <RefactoredIssueTableUserCellRenderer user={params.data.createdBy} />,
    filter: "agSetColumnFilter",
    context: ISSUE_SECTIONS.HISTORY,
    editable: false,
    minWidth: 140,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.ASSIGNEDTO_NAME),
    ...context.createUserColumnEditor("assignedTo"),
    cellRenderer: (params) => <RefactoredIssueTableUserCellRenderer user={params.data.assignedTo} />,
    minWidth: 160,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.LASTUPDATEDBY_NAME),
    cellRenderer: (params) => <RefactoredIssueTableUserCellRenderer user={params.data.lastUpdatedBy} />,
    filter: "agSetColumnFilter",
    context: ISSUE_SECTIONS.HISTORY,
    editable: false,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.OWNER_NAME),
    ...context.createUserColumnEditor("owner"),
    cellRenderer: (params) => <RefactoredIssueTableUserCellRenderer user={params.data.owner} />,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.ITEMMEMBERS),
    cellClass: "grid-display-block",
    valueFormatter: (params) => {
      if (params.value == null) {
        return "";
      }

      return params.value.map((user: IMinUserDepricated | IUserPartial) => user.name).join(", ");
    },
    valueGetter: (params) => params.data?.itemMembers?.map((user) => user.name),
    filter: "agSetColumnFilter",
    autoHeight: true,
    cellRenderer: (params) => (
      <McMantineMultiUserSelect
        displayVariant="tableCell"
        data={context.members
          .map((member) => member.user)
          .filter((user): user is IUserDepricated => user != null && typeof user !== "string")}
        defaultValue={params.data?.itemMembers ?? []}
        onChange={(selectedUsers) =>
          context.updateItemMembers(params.data._id, selectedUsers as unknown as IMinUserDepricated[])
        }
      />
    ),
  },
];

const getDateColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.CREATEDAT),
    width: 125,
    valueFormatter: dateExportFormatter,
    valueGetter: (params) => new Date(params.data.createdAt),
    cellRenderer: (params) => formatShortDate(params.value, true),
    filter: "agDateColumnFilter",
    context: ISSUE_SECTIONS.HISTORY,
    editable: false,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.UPDATEDAT),
    width: 125,
    valueFormatter: dateExportFormatter,
    valueGetter: (params) => new Date(params.data.updatedAt),
    filter: "agDateColumnFilter",
    cellRenderer: (params) => formatShortDate(params.value, true),
    context: ISSUE_SECTIONS.HISTORY,
    editable: false,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.DATES_PLANNEDSTART),
    width: 125,
    ...context.createDateColumnEditor("plannedStart"),
    filter: "agDateColumnFilter",
    cellRenderer: (params) => formatShortDate(params.value, true),
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.DATES_DUE),
    width: 127,
    filter: "agDateColumnFilter",
    ...context.createDateColumnEditor("due"),
    cellRenderer: (params) => {
      if (isValidDateValue(params.value) === false) {
        return "-";
      }

      return (
        <DisplayDueDate
          dueDate={params.value}
          isComplete={
            params.data != null &&
            (params.data.status === ISSUE_COMPLETED_VALUE || params.data.status === ISSUE_EXPIRED)
          }
        />
      );
    },
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.DATES_COMPLETED),
    ...context.createDateColumnEditor("completed"),
    filter: "agDateColumnFilter",
    cellRenderer: (params) => formatShortDate(params.value, true),
  },
];

const getTimeColumns = (context: IssueTableColumnContext): ColDef<any>[] => [
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.HOURS_ORIGNALESTIMATE),
    valueFormatter: (params) => (params.value != null ? params.value : 0),
    editable: true,
    filter: "agNumberColumnFilter",
    context: ISSUE_SECTIONS.TIME,
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.HOURS_COMPLETED),
    editable: true,
    context: ISSUE_SECTIONS.TIME,
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => (params.value != null ? params.value : 0),
  },
  {
    ...context.getIssueColumnBaseDef(ISSUE_DATAFILEDS_ENUM.HOURS_REMAINING),
    editable: true,
    context: ISSUE_SECTIONS.TIME,
    filter: "agNumberColumnFilter",
    valueFormatter: (params) => (params.value != null ? params.value : 0),
  },
];
